import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy, where } from 'firebase/firestore';

const CommunityServiceReports = ({ user, db, isAdmin, isCommunityServiceAdmin }) => {
    const [activeTab, setActiveTab] = useState('personal');
    const [allUserData, setAllUserData] = useState([]);
    const [personalData, setPersonalData] = useState([]);
    const [summaryStats, setSummaryStats] = useState({});
    const [loading, setLoading] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);

    useEffect(() => {
        loadPersonalData();
        if (isAdmin || isCommunityServiceAdmin) {
            loadAllUserData();
        }
    }, [user, isAdmin, isCommunityServiceAdmin]);

    const loadPersonalData = async () => {
        setLoading(true);
        try {
            const q = query(
                collection(db, 'community-service-logs'),
                where('userId', '==', user.email),
                orderBy('weekStartDate', 'desc')
            );
            const querySnapshot = await getDocs(q);
            const data = [];
            let totalHours = 0;

            querySnapshot.forEach((doc) => {
                const docData = doc.data();
                data.push(docData);
                totalHours += docData.weekTotal || 0;
            });

            setPersonalData(data);
        } catch (error) {
            console.error('Error loading personal data:', error);
        }
        setLoading(false);
    };

    const loadAllUserData = async () => {
        setLoading(true);
        try {
            const q = query(
                collection(db, 'community-service-logs'),
                orderBy('weekStartDate', 'desc')
            );
            const querySnapshot = await getDocs(q);
            const userMap = {};
            const weeklyData = [];

            querySnapshot.forEach((doc) => {
                const docData = doc.data();
                weeklyData.push(docData);

                if (!userMap[docData.userId]) {
                    userMap[docData.userId] = {
                        userId: docData.userId,
                        userName: docData.userName,
                        totalHours: 0,
                        weekCount: 0,
                        weeks: []
                    };
                }

                userMap[docData.userId].totalHours += docData.weekTotal || 0;
                userMap[docData.userId].weekCount += 1;
                userMap[docData.userId].weeks.push(docData);
            });

            const users = Object.values(userMap).sort((a, b) => b.totalHours - a.totalHours);
            setAllUserData(users);

            // Calculate summary stats
            const stats = calculateSummaryStats(users, weeklyData);
            setSummaryStats(stats);
        } catch (error) {
            console.error('Error loading all user data:', error);
        }
        setLoading(false);
    };

    const calculateSummaryStats = (users, weeklyData) => {
        if (users.length === 0) return {};

        const totalUsers = users.length;
        const totalHours = users.reduce((sum, user) => sum + user.totalHours, 0);
        const avgHoursPerUser = totalHours / totalUsers;

        const sortedByHours = [...users].sort((a, b) => b.totalHours - a.totalHours);
        const highest = sortedByHours[0];
        const lowest = sortedByHours[sortedByHours.length - 1];

        // Weekly averages
        const weeklyTotals = {};
        weeklyData.forEach(week => {
            if (!weeklyTotals[week.weekStartDate]) {
                weeklyTotals[week.weekStartDate] = 0;
            }
            weeklyTotals[week.weekStartDate] += week.weekTotal || 0;
        });

        const weeklyAverages = Object.values(weeklyTotals).sort((a, b) => b - a);

        return {
            totalUsers,
            totalHours,
            avgHoursPerUser,
            highest,
            lowest,
            weeklyAverages,
            mostRecentWeek: weeklyAverages[0] || 0
        };
    };

    const PersonalReport = () => {
        const totalHours = personalData.reduce((sum, week) => sum + (week.weekTotal || 0), 0);
        const avgPerWeek = personalData.length > 0 ? totalHours / personalData.length : 0;

        return (
            <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-blue-50 p-4 rounded-lg">
                        <div className="text-2xl font-bold text-blue-800">{totalHours}</div>
                        <div className="text-sm text-blue-600">Total Hours</div>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg">
                        <div className="text-2xl font-bold text-green-800">{personalData.length}</div>
                        <div className="text-sm text-green-600">Weeks Logged</div>
                    </div>
                    <div className="bg-purple-50 p-4 rounded-lg">
                        <div className="text-2xl font-bold text-purple-800">{avgPerWeek.toFixed(1)}</div>
                        <div className="text-sm text-purple-600">Avg Hours/Week</div>
                    </div>
                </div>

                <div className="bg-white border rounded-lg overflow-hidden">
                    <div className="px-4 py-3 bg-gray-50 border-b">
                        <h3 className="font-semibold text-gray-800">Weekly History</h3>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Week Of</th>
                                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Hours</th>
                                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Daily Breakdown</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {personalData.map((week, index) => (
                                    <tr key={index} className="hover:bg-gray-50">
                                        <td className="px-4 py-2 text-sm">
                                            {new Date(week.weekStartDate + 'T00:00:00').toLocaleDateString()}
                                        </td>
                                        <td className="px-4 py-2">
                                            <span className="text-sm font-medium text-[#782953]">
                                                {week.weekTotal || 0}h
                                            </span>
                                        </td>
                                        <td className="px-4 py-2 text-sm text-gray-600">
                                            {Object.entries(week.dailyLogs || {}).map(([date, data]) =>
                                                data.hours > 0 ? `${new Date(date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short' })}: ${data.hours}h` : null
                                            ).filter(Boolean).join(', ') || 'No hours logged'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {personalData.length === 0 && (
                        <div className="p-8 text-center text-gray-500">
                            No community service hours logged yet.
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const AllUsersReport = () => {
        if (!isAdmin && !isCommunityServiceAdmin) {
            return (
                <div className="text-center py-8 text-gray-500">
                    You don't have permission to view all user reports.
                </div>
            );
        }

        return (
            <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-blue-50 p-4 rounded-lg">
                        <div className="text-2xl font-bold text-blue-800">{summaryStats.totalUsers || 0}</div>
                        <div className="text-sm text-blue-600">Active Users</div>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg">
                        <div className="text-2xl font-bold text-green-800">{summaryStats.totalHours || 0}</div>
                        <div className="text-sm text-green-600">Total Hours</div>
                    </div>
                    <div className="bg-purple-50 p-4 rounded-lg">
                        <div className="text-2xl font-bold text-purple-800">{(summaryStats.avgHoursPerUser || 0).toFixed(1)}</div>
                        <div className="text-sm text-purple-600">Avg per User</div>
                    </div>
                    <div className="bg-orange-50 p-4 rounded-lg">
                        <div className="text-2xl font-bold text-orange-800">{summaryStats.mostRecentWeek || 0}</div>
                        <div className="text-sm text-orange-600">Latest Week Total</div>
                    </div>
                </div>

                {summaryStats.highest && summaryStats.lowest && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                            <h4 className="font-semibold text-green-800 mb-2">🏆 Highest Hours</h4>
                            <div className="text-lg font-bold text-green-700">{summaryStats.highest.userName}</div>
                            <div className="text-sm text-green-600">{summaryStats.highest.totalHours} hours total</div>
                        </div>
                        <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
                            <h4 className="font-semibold text-yellow-800 mb-2">📈 Needs Encouragement</h4>
                            <div className="text-lg font-bold text-yellow-700">{summaryStats.lowest.userName}</div>
                            <div className="text-sm text-yellow-600">{summaryStats.lowest.totalHours} hours total</div>
                        </div>
                    </div>
                )}

                <div className="bg-white border rounded-lg overflow-hidden">
                    <div className="px-4 py-3 bg-gray-50 border-b">
                        <h3 className="font-semibold text-gray-800">All Users Summary</h3>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">User</th>
                                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Total Hours</th>
                                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Weeks Logged</th>
                                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Avg/Week</th>
                                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {allUserData.map((userData, index) => (
                                    <tr key={userData.userId} className="hover:bg-gray-50">
                                        <td className="px-4 py-2">
                                            <div>
                                                <div className="text-sm font-medium text-gray-900">{userData.userName}</div>
                                                <div className="text-xs text-gray-500">{userData.userId}</div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-2">
                                            <span className="text-sm font-medium text-[#782953]">
                                                {userData.totalHours}h
                                            </span>
                                        </td>
                                        <td className="px-4 py-2 text-sm text-gray-600">
                                            {userData.weekCount}
                                        </td>
                                        <td className="px-4 py-2 text-sm text-gray-600">
                                            {(userData.totalHours / userData.weekCount).toFixed(1)}h
                                        </td>
                                        <td className="px-4 py-2">
                                            <button
                                                onClick={() => setSelectedUser(userData)}
                                                className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded hover:bg-blue-200"
                                            >
                                                View Details
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {allUserData.length === 0 && (
                        <div className="p-8 text-center text-gray-500">
                            No community service data found.
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const UserDetailModal = () => {
        if (!selectedUser) return null;

        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                        <h3 className="text-lg font-semibold text-gray-900">
                            {selectedUser.userName} - Detailed Report
                        </h3>
                        <button
                            onClick={() => setSelectedUser(null)}
                            className="text-gray-400 hover:text-gray-600"
                        >
                            ✕
                        </button>
                    </div>

                    <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                            <div className="bg-blue-50 p-4 rounded-lg">
                                <div className="text-2xl font-bold text-blue-800">{selectedUser.totalHours}</div>
                                <div className="text-sm text-blue-600">Total Hours</div>
                            </div>
                            <div className="bg-green-50 p-4 rounded-lg">
                                <div className="text-2xl font-bold text-green-800">{selectedUser.weekCount}</div>
                                <div className="text-sm text-green-600">Weeks Logged</div>
                            </div>
                            <div className="bg-purple-50 p-4 rounded-lg">
                                <div className="text-2xl font-bold text-purple-800">
                                    {(selectedUser.totalHours / selectedUser.weekCount).toFixed(1)}
                                </div>
                                <div className="text-sm text-purple-600">Avg Hours/Week</div>
                            </div>
                        </div>

                        <div className="bg-white border rounded-lg overflow-hidden">
                            <div className="px-4 py-3 bg-gray-50 border-b">
                                <h4 className="font-semibold text-gray-800">Weekly Breakdown</h4>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Week Of</th>
                                            <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Hours</th>
                                            <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Activities</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {selectedUser.weeks.map((week, index) => (
                                            <tr key={index} className="hover:bg-gray-50">
                                                <td className="px-4 py-2 text-sm">
                                                    {new Date(week.weekStartDate + 'T00:00:00').toLocaleDateString()}
                                                </td>
                                                <td className="px-4 py-2">
                                                    <span className="text-sm font-medium text-[#782953]">
                                                        {week.weekTotal || 0}h
                                                    </span>
                                                </td>
                                                <td className="px-4 py-2 text-sm text-gray-600">
                                                    <div className="space-y-1">
                                                        {Object.entries(week.dailyLogs || {}).map(([date, data]) =>
                                                            data.hours > 0 ? (
                                                                <div key={date}>
                                                                    <span className="font-medium">
                                                                        {new Date(date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short' })}: {data.hours}h
                                                                    </span>
                                                                    {data.comment && <span className="text-gray-500"> - {data.comment}</span>}
                                                                </div>
                                                            ) : null
                                                        ).filter(Boolean)}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="max-w-7xl mx-auto p-6">
            <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-[#782953]">Community Service Reports</h2>
                    <div className="text-sm text-gray-600">
                        Fall 2025
                    </div>
                </div>

                {/* Tab Navigation */}
                <div className="border-b border-gray-200 mb-6">
                    <nav className="-mb-px flex space-x-8">
                        <button
                            onClick={() => setActiveTab('personal')}
                            className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                                activeTab === 'personal'
                                    ? 'border-[#782953] text-[#782953]'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                        >
                            My Report
                        </button>
                        {(isAdmin || isCommunityServiceAdmin) && (
                            <button
                                onClick={() => setActiveTab('all-users')}
                                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                                    activeTab === 'all-users'
                                        ? 'border-[#782953] text-[#782953]'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                            >
                                All Users
                            </button>
                        )}
                    </nav>
                </div>

                {/* Tab Content */}
                {loading ? (
                    <div className="flex justify-center items-center h-64">
                        <div className="text-lg text-gray-600">Loading reports...</div>
                    </div>
                ) : (
                    <>
                        {activeTab === 'personal' && <PersonalReport />}
                        {activeTab === 'all-users' && <AllUsersReport />}
                    </>
                )}
            </div>

            {/* User Detail Modal */}
            <UserDetailModal />
        </div>
    );
};

export default CommunityServiceReports;
